import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/common/Skeleton';
import { WebBodyText14 } from '@fintronners/react-ui/src/GlobalStyling/webTypography';
import usePreviewTradeFee from '@fintronners/react-widgets/src/hooks/trades/usePreviewTradeFee';
import {
  SecurityMutualFundDetails,
  type V1PreviewTradeFeeResponse,
} from '@fintronners/react-api/src';

import {
  NumberNoNaN,
  thousandFormatNumbroCurrency,
} from '@fintronners/react-utils/src/numberUtilsTSX';

import useTradeApiService from '@/hooks/api/useTradeService';
import useGrowthBookFeature from '@fintronners/react-config/src/hooks/useGrowthBookFeature';
import { FeatureFlags } from '@fintronners/react-config/src/Config/growthbook';
import { usePreviewTradeFeeStore } from '@/stores/trades/preview-trade-fee';
import { getLoadTypeDisplayValue } from '@fintronners/internal-core/src/screeners/utils';

type MutualFundsDetailsWidgetProps = {
  data: SecurityMutualFundDetails & { tradableInCash: number };
  loading?: boolean;
  amount?: number;
  minimumTradeAmount?: number;
  assetUid?: string;
};

//FIXME: Use i18n instead of hardcoded strings
const displayStrings = {
  grossExpRatio: 'Exp ratio (gross):',
  netExpRatio: 'Exp ratio (net):',
  distributionFee: 'Distribution/service fee (12b-1):',
  initialInvestment: 'Minimum initial investment:',
  subsequentInvestment: 'Minimum subsequent investment:',
  transactionFee: 'Transaction fee:',
  loadType: 'Load type:',
  load: 'Load:',
  noFee: 'No Fee',
};

const MutualFundDetailsWidget = ({
  data,
  loading = false,
  amount,
  minimumTradeAmount,
  assetUid,
}: MutualFundsDetailsWidgetProps) => {
  const { t } = useTranslation();

  const initialFeeFetched = useRef<boolean>(false);

  const isFeesAndCommissionsEnabled = useGrowthBookFeature(
    FeatureFlags.isFeesAndCommissionsEnabled,
  );

  const tradeApiService = useTradeApiService();

  const {
    data: feesAndCommissions,
    loading: isTradeFeeLoading,
    setData,
    setLoading,
  } = usePreviewTradeFeeStore();

  const onRequestInvoked = () => setLoading(true);
  const onRequestEnd = () => setLoading(false);
  const onSuccess = (feeData: V1PreviewTradeFeeResponse) => setData(feeData);

  /*
   * On request failure, the fee preview is not applied.
   * The form keeps its default values without fees
   * until the amount input changes.
   */
  const { getTradeFee } = usePreviewTradeFee({
    withDebounce: true,
    apiService: tradeApiService,
    shouldSkip: !isFeesAndCommissionsEnabled,
    onRequestInvoked,
    onSuccess,
    onRequestEnd,
  });

  /**
   * Fetch initial fee for mutual fund buys to show accurate breakdown
   * when the form loads with the minimum trade amount.
   */
  useEffect(() => {
    const shouldFetchInitialFee = !initialFeeFetched.current && isFeesAndCommissionsEnabled;
    const hasRequiredParams = minimumTradeAmount !== undefined && assetUid !== undefined;
    const isAmountSet = amount;

    if (!shouldFetchInitialFee || !hasRequiredParams || isAmountSet) {
      return;
    }

    initialFeeFetched.current = true;

    getTradeFee({
      amount: minimumTradeAmount.toString(),
      assetUid,
    });
  }, [amount, minimumTradeAmount, getTradeFee, assetUid, isFeesAndCommissionsEnabled]);

  const transactionFee = NumberNoNaN(feesAndCommissions?.feeAmount, 0);

  if (loading || isTradeFeeLoading) {
    return <Skeleton className="h-64 w-full" />;
  }

  return (
    <div className="flex flex-col gap-1 bg-grey7 p-2.5 rounded-lg">
      <div className="flex justify-between">
        <WebBodyText14.Regular className="text-grey72">
          {t('generalInvestments.stockScreen.availableCash')}
        </WebBodyText14.Regular>

        <WebBodyText14.Bold className="text-darkestBlue">
          {thousandFormatNumbroCurrency(data.tradableInCash)}
        </WebBodyText14.Bold>
      </div>

      <div className="flex justify-between">
        <WebBodyText14.Regular className="text-grey72">
          {displayStrings.grossExpRatio}
        </WebBodyText14.Regular>

        <WebBodyText14.Bold className="text-darkestBlue">
          {`${data?.expenseRatioGross}%`}
        </WebBodyText14.Bold>
      </div>

      <div className="flex justify-between">
        <WebBodyText14.Regular className="text-grey72">
          {displayStrings.netExpRatio}
        </WebBodyText14.Regular>

        <WebBodyText14.Bold className="text-darkestBlue">
          {`${data?.expenseRatioNet}%`}
        </WebBodyText14.Bold>
      </div>

      <div className="flex justify-between">
        <WebBodyText14.Regular className="text-grey72">
          {displayStrings.distributionFee}
        </WebBodyText14.Regular>

        <WebBodyText14.Bold className="text-darkestBlue">
          {`${data?.distributionServiceFee}%`}
        </WebBodyText14.Bold>
      </div>

      <div className="flex justify-between">
        <WebBodyText14.Regular className="text-grey72">
          {displayStrings.initialInvestment}
        </WebBodyText14.Regular>

        <WebBodyText14.Bold className="text-darkestBlue">
          {thousandFormatNumbroCurrency(data?.initialInvestmentMinimum)}
        </WebBodyText14.Bold>
      </div>

      <div className="flex justify-between">
        <WebBodyText14.Regular className="text-grey72">
          {displayStrings.subsequentInvestment}
        </WebBodyText14.Regular>

        <WebBodyText14.Bold className="text-darkestBlue">
          {thousandFormatNumbroCurrency(data?.additionalInvestmentMinimum)}
        </WebBodyText14.Bold>
      </div>

      <div className="flex justify-between">
        <WebBodyText14.Regular className="text-grey72">
          {displayStrings.transactionFee}
        </WebBodyText14.Regular>

        <WebBodyText14.Bold className="text-darkestBlue">
          {transactionFee > 0 ? thousandFormatNumbroCurrency(transactionFee) : displayStrings.noFee}
        </WebBodyText14.Bold>
      </div>

      <div className="flex justify-between">
        <WebBodyText14.Regular className="text-grey72">
          {displayStrings.loadType}
        </WebBodyText14.Regular>

        <WebBodyText14.Bold className="text-darkestBlue">
          {getLoadTypeDisplayValue(data?.loadType)}
        </WebBodyText14.Bold>
      </div>

      <div className="flex justify-between">
        <WebBodyText14.Regular className="text-grey72">{displayStrings.load}</WebBodyText14.Regular>
        <WebBodyText14.Bold className="text-darkestBlue">{`${data?.loadPct}%`}</WebBodyText14.Bold>
      </div>
    </div>
  );
};

export default MutualFundDetailsWidget;
